import Cookies from "js-cookie";
import logger from "@/lib/logger";
import { getEnv } from "@/env";
import newrelic from "@/lib/monitoring/newrelic";
import { jwtDecode } from "jwt-decode";

/*
example cookie:

"{\"version\": 1\054 \"username\": \"example-username\"\054 \"email\": \"example@email.com\"\054 \"header_urls\": {\"logout\": \"https://courses.edx.org/logout\"\054 \"account_settings\": \"https://courses.edx.org/account/settings\"\054 \"learner_profile\": \"https://courses.edx.org/u/example-username\"\054 \"resume_block\": \"https://courses.edx.org/courses/etc\"}\054 \"user_image_urls\": {\"full\": \"https://test.com/example.jpg\"\054 \"large\": \"https://test.com/example.jpg\"\054 \"medium\": \"https://test.com/example.jpg\"\054 \"small\": \"https://test.com/example.jpg\"}}"

*/

interface RawUserInfo {
  username: string;
  email: string;
  header_urls: {
    logout: string;
    account_settings: string;
    learner_profile: string;
    resume_block: string;
  };
  user_image_urls: {
    full: string;
    large: string;
    medium: string;
    small: string;
  };
}

const transformUserInfoCookie = (rawCookie: string): RawUserInfo | null => {
  try {
    // Fix any encoded commas/quotes that have been double escaped before parsing.
    return JSON.parse(rawCookie.replace(/\\054/g, ",").replace(/\\"/g, '"'));
  } catch (e) {
    logger.warn("Failed to parse user info cookie", e);

    // New Relic logging
    newrelic.noticeError(e as Error, {
      submodule: "auth",
      context: "parseUserInfoCookie",
      cookieLength: rawCookie ? rawCookie.length : 0,
      cookieExists: !!rawCookie,
      timestamp: new Date().toISOString(),
    });

    return null;
  }
};

interface UserInfo {
  username: string;
  email: string;
  headerUrls?: {
    logout: string;
    accountSettings: string;
    learnerProfile: string;
    resumeBlock: string;
  };
  userImageUrls?: {
    full: string;
    large: string;
    medium: string;
    small: string;
  };
}

//TODO: Review auth source of truth for this interface
export interface EdxAccessTokenPayload {
  aud: string;
  exp: number;
  grant_type: string;
  iat: number;
  iss: string;
  preferred_username: string;
  scopes: string[];
  version: string;
  sub: string;
  filters: string[];
  is_restricted: boolean;
  email_verified: boolean;
  user_id: number;
  email: string;
  name: string;
  family_name: string;
  given_name: string;
  administrator: boolean;
  superuser: boolean;
  [key: string]: unknown;
}

const USER_INFO_COOKIE_NAME = getEnv("NEXT_PUBLIC_USER_INFO_COOKIE_NAME");

export const getUserInfoFromJWT = (): EdxAccessTokenPayload | null => {
  try {
    const accessTokenCookieName = getEnv(
      "NEXT_PUBLIC_ACCESS_TOKEN_COOKIE_NAME",
    );
    const token = Cookies.get(accessTokenCookieName);
    if (!token) {
      return null;
    }

    const decoded = jwtDecode<EdxAccessTokenPayload>(token);
    return decoded ?? null;
  } catch (error) {
    logger.warn("Failed to decode access token JWT", error);
    newrelic.noticeError(error as Error, {
      submodule: "auth",
      context: "getUserInfoFromJWT",
      timestamp: new Date().toISOString(),
    });
    return null;
  }
};

const getUserInfo = (): UserInfo | null => {
  try {
    const rawCookie = Cookies.get(USER_INFO_COOKIE_NAME);
    if (!rawCookie) {
      return null;
    }

    const cookieData = transformUserInfoCookie(rawCookie);
    if (cookieData) {
      const userInfo: UserInfo = {
        username: cookieData.username,
        email: cookieData.email,
      };
      if (cookieData.header_urls) {
        userInfo.headerUrls = {
          logout: cookieData.header_urls.logout,
          accountSettings: cookieData.header_urls.account_settings,
          learnerProfile: cookieData.header_urls.learner_profile,
          resumeBlock: cookieData.header_urls.resume_block,
        };
      }
      if (cookieData.user_image_urls) {
        userInfo.userImageUrls = {
          full: cookieData.user_image_urls.full,
          large: cookieData.user_image_urls.large,
          medium: cookieData.user_image_urls.medium,
          small: cookieData.user_image_urls.small,
        };
      }
      return userInfo;
    }
    return null;
  } catch (error) {
    // Network error logging
    newrelic.noticeError(error as Error, {
      submodule: "auth",
      context: "getUserInfo",
      cookieName: USER_INFO_COOKIE_NAME,
      timestamp: new Date().toISOString(),
    });

    return null;
  }
};

export default getUserInfo;
